import { useState } from "react";
import { Form, Formik } from "formik";
import { Button } from "@/components/ui/button";
import * as Yup from "yup";
import { InfoSolid } from "@/components/icons/info-solid";
import { SelectInput, Option } from "@/components/inputs/select";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { ExternalLink } from "lucide-react";
import type { ShipmentBasicInfo } from "../types/shipment-calculator.type";

function CalculatorStart({
  setStep,
  setShipmentBasicInfo,
  defaultCountry,
}: {
  setStep: React.Dispatch<React.SetStateAction<number>>;
  setShipmentBasicInfo: React.Dispatch<React.SetStateAction<ShipmentBasicInfo>>;
  defaultCountry?: string;
}) {
  const [method, setMethod] = useState("Air");
  const validationSchema = Yup.object({
    method: Yup.string().required(),
    kind: method === "Air" ? Yup.string().required() : Yup.string(),
    country: Yup.string().oneOf(["NG", "GH"], "Invalid country selected").required(),
  });

  const shippingOptions: Option[] = [
    {
      value: "Air",
      label: "Air",
    },
    { value: "Sea", label: "Sea" },
  ];

  const airShipmentOptions: Option[] = [
    {
      value: "Normal",
      label: "Normal",
    },
    { value: "Special", label: "Special" },
  ];

  const destinationCountryOptions: Option[] = [
    {
      value: "NG",
      label: "Nigeria",
    },
    {
      value: "GH",
      label: "Ghana",
    },
  ];

  const isMobile = useMediaQuery("(max-width: 768px)");
  const selectInputClassName =
    "flex flex-col items-start justify-start gap-2 w-full text-primary-text-900";
  const selectInputHeight = isMobile ? "lg" : "xl";
  return (
    <Formik
      initialValues={{ method: "", kind: "", country: defaultCountry === "GH" ? "GH" : "NG" } as ShipmentBasicInfo}
      validationSchema={validationSchema}
      onSubmit={(values) => {
        setStep(2);
        setShipmentBasicInfo({ ...values, kind: method === "Sea" ? "" : values.kind });
        return;
      }}
    >
      <Form className="mx-auto flex w-full max-w-[506px] flex-col items-center justify-center gap-6 px-3 sm:px-0">
        <SelectInput
          name="method"
          label="Select Shipping Method"
          variant="primary"
          options={shippingOptions}
          height={selectInputHeight}
          className={selectInputClassName}
          placeholder="Select Shipping Method"
          onChange={setMethod}
        />
        {method === "Air" && (
          <SelectInput
            name="kind"
            label="What kind of shipment is this?"
            variant="primary"
            options={airShipmentOptions}
            height={selectInputHeight}
            className={selectInputClassName}
            placeholder="Select Shipment type"
          >
            <a
              href={`${process.env.NEXT_PUBLIC_WEBSITE_BASE_URL}/guides?tab=ship`}
              target="_blank"
              className="ml-3 inline-flex items-center gap-1 align-middle text-[10px] font-normal text-primary underline underline-offset-2 sm:text-xs"
            >
              See guide on different kind of air shipment
              <ExternalLink className="h-3 w-3" aria-hidden="true" />
            </a>
          </SelectInput>
        )}
        <SelectInput
          name="country"
          label="Destination Country"
          variant="primary"
          options={destinationCountryOptions}
          height={selectInputHeight}
          className={selectInputClassName}
          placeholder="Select destination country"
        />
        <div className="flex w-full flex-row items-start justify-center gap-2 rounded-lg bg-[#fff9e9] px-4 py-2 text-lg text-[#f8b400]">
          <div className="shrink-0">
            <InfoSolid fill="#FFB400" />
          </div>
          <p className="text-xs leading-snug text-black lg:text-[13px]">
            Please ensure you select an accurate shipment method, so as to get an accurate
            calculation.
          </p>
        </div>
        <Button
          type="submit"
          radius="full"
          className={`mx-auto w-full bg-[#0f4694] text-lg font-normal capitalize hover:bg-[#0d3d82] sm:text-xl ${isMobile ? "h-[58px]" : "h-[59px]"}`}
        >
          Continue
        </Button>
      </Form>
    </Formik>
  );
}

export default CalculatorStart;

